import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BarChart3, RefreshCcw, TrendingUp, PackageCheck, Clock, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getSalesAnalytics, getFirms } from '../../services/taskApi';

const fmtINR = (v) => {
  const n = Number(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const StackBar = ({ e, max }) => {
  const w = (v) => `${max > 0 ? Math.max((v / max) * 100, v > 0 ? 1 : 0) : 0}%`;
  return (
    <div className="h-2.5 w-full rounded-full bg-gray-500/10 overflow-hidden flex">
      <div className="bg-green-500 h-full" style={{ width: w(e.delivered) }} title={`Delivered ${fmtINR(e.delivered)}`} />
      <div className="bg-orange-400 h-full" style={{ width: w(e.pending) }} title={`Pending ${fmtINR(e.pending)}`} />
      <div className="bg-red-500 h-full" style={{ width: w(e.short_closed) }} title={`Short closed ${fmtINR(e.short_closed)}`} />
    </div>
  );
};

export default function FlowSalesAnalytics() {
  const { user, isDark } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  const [firms, setFirms] = useState([]);
  const [filterCompany, setFilterCompany] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null); // {agent_id, agent_name}
  const [data, setData] = useState(null);
  const [agentMonthly, setAgentMonthly] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useCallback((agent) => {
    const p = {};
    if (filterCompany) p.company_id = filterCompany;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (agent) p.agent_id = agent;
    return p;
  }, [filterCompany, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, fRes] = await Promise.all([getSalesAnalytics(params()), getFirms()]);
      setData(aRes.data);
      setFirms(fRes.data || []);
      setSelectedAgent(null);
      setAgentMonthly(null);
    } catch { /* swallow */ }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pickAgent = async (e) => {
    if (!isAdmin) return;
    if (selectedAgent?.agent_id === e.agent_id) { setSelectedAgent(null); setAgentMonthly(null); return; }
    setSelectedAgent(e);
    try {
      const res = await getSalesAnalytics(params(e.agent_id));
      setAgentMonthly(res.data.monthly || []);
    } catch { /* swallow */ }
  };

  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : '';
  const bg = isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200';
  const monthly = selectedAgent ? (agentMonthly || []) : (data?.monthly || []);
  const monthMax = Math.max(1, ...monthly.map(m => m.generated));
  const engMax = Math.max(1, ...(data?.by_engineer || []).map(e => e.generated));

  const summary = data ? [
    { label: 'Order Value Generated', value: data.totals.generated, icon: TrendingUp, color: 'text-blue-500' },
    { label: 'Delivered Value', value: data.totals.delivered, icon: PackageCheck, color: 'text-green-500' },
    { label: 'Pending Value', value: data.totals.pending, icon: Clock, color: 'text-orange-500' },
    { label: 'Short Closed Value', value: data.totals.short_closed, icon: XCircle, color: 'text-red-500' },
  ] : [];

  return (
    <div className="space-y-4" data-testid="sales-analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <BarChart3 size={20} className="text-blue-500" /> Sales Analytics
          </h1>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            {isAdmin ? 'Engineer-wise order value — click an engineer for their monthly trend' : 'Your order value performance'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select className={`h-9 rounded-lg text-xs px-3 border ${isDark ? 'bg-white/5 text-white border-white/10' : 'bg-white border-gray-200'}`}
            value={filterCompany} onChange={e => setFilterCompany(e.target.value)} data-testid="analytics-company-filter">
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={`h-9 w-36 text-xs ${inputCls}`} data-testid="analytics-date-from" />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={`h-9 w-36 text-xs ${inputCls}`} data-testid="analytics-date-to" />
          <Button size="sm" variant="outline" onClick={fetchData} data-testid="analytics-refresh"><RefreshCcw size={12} /></Button>
        </div>
      </div>

      {loading ? (
        <div className={`text-center py-16 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Loading analytics…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {summary.map(s => (
              <Card key={s.label} className={bg} data-testid={`analytics-card-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <s.icon size={15} className={s.color} />
                    <span className={`text-[11px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{s.label}</span>
                  </div>
                  <div className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmtINR(s.value)}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className={bg}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Engineer-wise Performance <span className={`text-xs font-normal ${isDark ? 'text-white/40' : 'text-gray-400'}`}>({data.totals.orders} orders)</span>
                </h3>
                <div className="flex items-center gap-3 text-[10px]">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Delivered</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" /> Pending</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Short closed</span>
                </div>
              </div>
              <div className="space-y-2">
                {data.by_engineer.map(e => (
                  <button key={e.agent_id || e.agent_name} onClick={() => pickAgent(e)}
                    className={`w-full text-left p-3 rounded-xl border transition-colors ${
                      selectedAgent?.agent_id === e.agent_id
                        ? (isDark ? 'border-blue-500/50 bg-blue-500/10' : 'border-blue-400 bg-blue-50')
                        : (isDark ? 'border-white/10 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50')
                    }`}
                    data-testid={`analytics-engineer-${(e.agent_name || 'unknown').toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                      <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{e.agent_name || e.agent_id || 'Unknown'}</span>
                      <div className="flex items-center gap-3 text-[11px]">
                        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>{e.orders} orders · {e.items} items</span>
                        <span className="font-bold text-blue-500">{fmtINR(e.generated)}</span>
                      </div>
                    </div>
                    <StackBar e={e} max={engMax} />
                    <div className={`flex flex-wrap gap-3 mt-1.5 text-[10px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      <span className="text-green-500 font-medium">Delivered {fmtINR(e.delivered)}</span>
                      <span className="text-orange-400 font-medium">Pending {fmtINR(e.pending)}</span>
                      {e.short_closed > 0 && <span className="text-red-500 font-medium">Short closed {fmtINR(e.short_closed)}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className={bg}>
            <CardContent className="p-4">
              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="monthly-trend-title">
                Monthly Trend {selectedAgent ? `— ${selectedAgent.agent_name}` : '(all)'}
              </h3>
              {monthly.length === 0 ? (
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>No data for this range</p>
              ) : (
                <div className="flex items-end gap-2 h-40 overflow-x-auto pb-1" data-testid="monthly-trend-chart">
                  {monthly.map(m => (
                    <div key={m.month} className="flex flex-col items-center gap-1 min-w-[52px] flex-1 h-full justify-end"
                      title={`${m.month}: generated ${fmtINR(m.generated)} · delivered ${fmtINR(m.delivered)} · pending ${fmtINR(m.pending)}`}>
                      <span className={`text-[9px] font-medium ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{fmtINR(m.generated)}</span>
                      <div className="w-full flex items-end gap-0.5" style={{ height: '75%' }}>
                        <div className="flex-1 rounded-t bg-blue-500/80" style={{ height: `${(m.generated / monthMax) * 100}%` }} />
                        <div className="flex-1 rounded-t bg-green-500/80" style={{ height: `${(m.delivered / monthMax) * 100}%` }} />
                      </div>
                      <span className={`text-[9px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{m.month}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
